import { z } from 'zod';
import { deliveryStatusFor, toAttempt } from './notification-activity-helpers.ts';

const timestamp = z.string().datetime({ offset: true });
export const historyCursor = z.object({ at: timestamp, id: z.string().uuid() });
export const eventCursor = z.object({ at: timestamp, id: z.string().min(1).max(200) });
export const historyRequest = z.object({
  action: z.enum(['list', 'detail']), loanId: z.string().trim().min(1).max(200),
  query: z.string().trim().max(200).default(''), from: timestamp.nullable().default(null),
  until: timestamp.nullable().default(null), cursor: historyCursor.nullable().default(null),
  messageId: z.string().uuid().optional(), eventCursor: eventCursor.nullable().default(null),
}).strict().refine(v => v.action !== 'detail' || Boolean(v.messageId), 'Message required')
  .refine(v => !v.from || !v.until || Date.parse(v.from) < Date.parse(v.until), 'Invalid date range');
const delivery = z.object({ state: z.enum(['delivered', 'bounced', 'complained', 'pending', 'unknown']), at: timestamp.nullable() });
const event = z.object({ svix_id: z.string(), event_type: z.string(), occurred_at: timestamp, recipient_email: z.string().nullable() });
export const historyItem = z.object({
  id: z.string().uuid(), createdAt: timestamp, subject: z.string().nullable(), status: z.string().nullable(),
  sentAt: timestamp.nullable(), direction: z.string(), toEmails: z.array(z.string()), ccEmails: z.array(z.string()),
  notificationType: z.string(), isTest: z.boolean(), delivery: delivery.nullable(), html: z.string().nullable(),
  contentKind: z.enum(['html', 'text_summary']),
  recipients: z.array(z.object({ email: z.string(), role: z.enum(['To', 'CC']), delivery: delivery.nullable() })),
  events: z.array(event), nextEventCursor: eventCursor.nullable(),
});
export const historyResponse = z.object({ items: z.array(historyItem), nextCursor: historyCursor.nullable() });
export type HistoryItem = z.infer<typeof historyItem>;
export type HistoryQuery = z.input<typeof historyRequest>;
export type HistoryResponse = z.infer<typeof historyResponse>;
const databaseRows = z.array(z.object({
  id: z.string().uuid(), created_at: timestamp, delivered_at: timestamp.nullable(), status: z.string().nullable(),
  subject: z.string().nullable(), email_address: z.string().nullable(), metadata: z.record(z.unknown()).nullable(),
  content: z.string().nullable(), direction: z.string(),
  events: z.array(z.object({ resend_id: z.string(), event_type: z.string(), occurred_at: timestamp, recipient_email: z.string().nullable() })),
  eventPage: z.array(event).nullable(),
}));

/** Keep recorded workflow state separate from provider evidence. The admin formatter's
 * default created_at -> sentAt assumption is not valid for a history containing drafts. */
export function historyPage(raw: unknown, now = Date.now()): HistoryResponse {
  const rows = databaseRows.parse(raw);
  const items = rows.slice(0, 50).map(row => {
    const attempt = toAttempt(row);
    const metadata = row.metadata ?? {};
    const inbound = row.direction === 'inbound';
    const recordedSent = ['sent', 'delivered'].includes(row.status?.toLowerCase() ?? '');
    const providerEvents = row.events.filter(event => event.resend_id === attempt.resendId);
    const hasProviderId = [attempt.resendId, metadata.gmailMessageId, metadata.gmail_message_id]
      .some(value => typeof value === 'string' && value.trim().length > 0);
    const accepted = !inbound && (recordedSent || hasProviderId || providerEvents.length > 0);
    const sentEvent = providerEvents.filter(event => event.event_type === 'email.sent')
      .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))[0];
    const sentAt = accepted ? row.delivered_at ?? sentEvent?.occurred_at ?? (recordedSent ? row.created_at : null) : null;
    const outcome = (email: string) => !accepted ? null : attempt.resendId
      ? deliveryStatusFor({ ...attempt, sentAt, toEmails: [email] }, providerEvents, now)
      : { state: 'unknown' as const, at: null };
    // These Gmail producers store a plain-text audit summary, not the sent MIME/HTML.
    const contentKind = metadata.channel === 'gmail_attachment'
      ? 'text_summary' as const : 'html' as const;
    const events = row.eventPage ?? [];
    const last = events[49];
    const to = [...new Set(attempt.toEmails)];
    const cc = [...new Set(attempt.ccEmails)].filter(email => !to.includes(email));
    return {
      id: row.id, createdAt: row.created_at, subject: row.subject, status: row.status, direction: row.direction,
      sentAt, toEmails: to, ccEmails: cc, notificationType: attempt.notificationType,
      isTest: Boolean(attempt.context.is_test), contentKind,
      delivery: outcome(to[0] ?? ''), html: row.content,
      recipients: [...to.map(email => ({ email, role: 'To' as const })), ...cc.map(email => ({ email, role: 'CC' as const }))]
        .map(recipient => ({ ...recipient, delivery: outcome(recipient.email) })),
      events: events.slice(0, 50), nextEventCursor: events.length > 50 ? { at: last.occurred_at, id: last.svix_id } : null,
    };
  });
  const last = items[49];
  return { items, nextCursor: rows.length > 50 ? { at: last.createdAt, id: last.id } : null };
}
